import React, { useState, useCallback, useEffect } from 'react';
import { FaHandPaper } from 'react-icons/fa';
import UnityGameDockApp from './UnityGameDockApp';

export interface SocialInteractionProps {
  /** When chat is unavailable or disconnected */
  disabled?: boolean;
  /** Called with the new raised state after a successful send */
  onHandRaiseChange?: (raised: boolean) => Promise<void>;
  /**
   * When set, keeps the Hand tile in sync with the app queue (e.g. moderator lowered your hand for everyone).
   */
  syncRaisedFromQueue?: boolean;
  dockMode?: boolean;
  dataTourId?: string;
}

const SocialInteraction: React.FC<SocialInteractionProps> = ({
  disabled = false,
  onHandRaiseChange,
  syncRaisedFromQueue,
  dockMode = true,
  dataTourId,
}) => {
  const [raised, setRaised] = useState(false);
  const [pending, setPending] = useState(false);
  const [errorFlash, setErrorFlash] = useState(false);

  useEffect(() => {
    if (syncRaisedFromQueue === undefined) return;
    setRaised(syncRaisedFromQueue);
  }, [syncRaisedFromQueue]);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled || pending || !onHandRaiseChange) return;

      const next = !raised;
      setPending(true);
      setRaised(next);
      try {
        await onHandRaiseChange(next);
      } catch {
        setRaised(!next);
        setErrorFlash(true);
        window.setTimeout(() => setErrorFlash(false), 2000);
      } finally {
        setPending(false);
      }
    },
    [disabled, pending, onHandRaiseChange, raised]
  );

  const canUse = Boolean(onHandRaiseChange) && !disabled;
  const title =
    !onHandRaiseChange || disabled
      ? 'Raise hand (connect to chat in this room)'
      : raised
        ? 'Lower hand'
        : 'Raise hand';

  if (dockMode) {
    return (
      <UnityGameDockApp
        dataTourId={dataTourId}
        ariaLabel={title}
        label="Hand"
        icon={
          <FaHandPaper
            className={raised ? 'unity-dock-hand-icon--raised' : undefined}
          />
        }
        pressed={raised}
        disabled={!canUse || pending}
        wrapperClassName={errorFlash ? 'ring-2 ring-red-400 rounded-xl' : ''}
        tileClassName={
          raised
            ? 'bg-gradient-to-br from-amber-300 to-orange-500 [&_span]:text-gray-900'
            : 'bg-gradient-to-br from-sky-600 to-blue-800'
        }
        onClick={handleClick}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!canUse || pending}
      aria-pressed={raised}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 font-semibold shadow-lg transition-all duration-200 ${
        raised
          ? 'bg-amber-500 bg-opacity-95 text-black hover:bg-opacity-100'
          : 'bg-blue-600 bg-opacity-80 text-white hover:bg-opacity-100'
      } ${errorFlash ? 'ring-2 ring-red-400' : ''} ${!canUse || pending ? 'cursor-not-allowed opacity-50' : ''}`}
      title={title}
    >
      <FaHandPaper
        size={16}
        className={raised ? 'unity-dock-hand-icon--raised' : undefined}
      />
      <span className="hidden sm:inline">{raised ? 'Lower hand' : 'Raise hand'}</span>
    </button>
  );
};

export default SocialInteraction;
